import type { SessionMode } from '../../utils/trainingProtocol';
import type { ChallengeTier } from './types';
import { clamp } from './rules';

export interface MazeBoard {
  size: number;
  passages: number[][];
  start: number;
  exit: number;
  checkpoints: number[];
  optimalPath: number[];
}
export function getMazeRules(mode: SessionMode, tier: ChallengeTier) {
  const level = mode === 'standard' ? 0 : tier;
  return {
    level, size: level === 0 ? 4 : level === 1 ? 5 : 6,
    checkpoints: level === 0 ? 0 : level === 1 ? 1 : 2,
    previewMs: [7000, 6500, 6000, 5000][level],
    fogRadius: level >= 2 ? 1 : null,
    scans: level === 0 ? 0 : level === 1 ? 0 : level === 2 ? 2 : 1,
    wallLimit: [8, 6, 5, 4][level],
    secondsPerStep: [4.2, 3.5, 3.1, 2.7][level],
    detourFactor: [3, 2.6, 2.2, 1.9][level],
  };
}

export function adjacentCell(cell: number, dx: number, dy: number, size: number): number | null {
  if (Math.abs(dx) + Math.abs(dy) !== 1) return null;
  const x = cell % size + dx, y = Math.floor(cell / size) + dy;
  return x < 0 || y < 0 || x >= size || y >= size ? null : y * size + x;
}

export function shortestMazePath(passages: number[][], start: number, end: number): number[] {
  const queue = [start];
  const parent = new Map<number, number>([[start, -1]]);
  for (let head = 0; head < queue.length; head++) {
    const node = queue[head];
    if (node === end) break;
    for (const neighbor of passages[node] ?? []) {
      if (!parent.has(neighbor)) { parent.set(neighbor, node); queue.push(neighbor); }
    }
  }
  if (!parent.has(end)) return [];
  const path: number[] = [];
  for (let node = end; node !== -1; node = parent.get(node)!) path.push(node);
  return path.reverse();
}

export function mazeObjectivePath(passages: number[][], start: number, objectives: number[]): number[] {
  const path = [start];
  let current = start;
  for (const target of objectives) {
    const leg = shortestMazePath(passages, current, target);
    if (!leg.length) return [];
    path.push(...leg.slice(1)); current = target;
  }
  return path;
}

export function createMaze(size: number, checkpointCount: number, random = Math.random): MazeBoard {
  const passages: number[][] = Array.from({ length: size * size }, () => []);
  const stack = [0], visited = new Set([0]);
  // Randomized DFS creates a connected spanning tree, never a blocked random board.
  while (stack.length) {
    const current = stack[stack.length - 1];
    const neighbors = [[1,0],[-1,0],[0,1],[0,-1]].flatMap(([dx,dy]) => {
      const next = adjacentCell(current, dx, dy, size);
      return next !== null && !visited.has(next) ? [next] : [];
    });
    if (!neighbors.length) { stack.pop(); continue; }
    const next = neighbors[Math.min(neighbors.length - 1, Math.floor(random() * neighbors.length))];
    passages[current].push(next); passages[next].push(current);
    visited.add(next); stack.push(next);
  }
  const start = 0;
  const farthest = Array.from({ length: size * size }, (_, i) => i).sort((a,b) => shortestMazePath(passages, start, b).length - shortestMazePath(passages, start, a).length);
  const exit = farthest[0];
  const direct = shortestMazePath(passages, start, exit);
  // Ordered landmarks lie on a guaranteed path; wrong-order visits cannot trap a run.
  const checkpoints = Array.from({ length: checkpointCount }, (_, i) => direct[Math.floor((i + 1) * (direct.length - 1) / (checkpointCount + 1))]);
  return { size, passages, start, exit, checkpoints, optimalPath: mazeObjectivePath(passages, start, [...checkpoints, exit]) };
}

export function mazeBudget(board: MazeBoard, rules: ReturnType<typeof getMazeRules>) {
  const optimal = board.optimalPath.length - 1;
  return {
    optimal,
    maxSteps: Math.ceil(optimal * rules.detourFactor),
    timeMs: Math.round((20 + optimal * rules.secondsPerStep) * 1000),
  };
}
export function mazeVisibility(board: MazeBoard, player: number, radius: number | null): Set<number> {
  if (radius === null) return new Set(board.passages.map((_, i) => i));
  return new Set(board.passages.flatMap((_, i) => Math.abs(i % board.size - player % board.size) + Math.abs(Math.floor(i / board.size) - Math.floor(player / board.size)) <= radius ? [i] : []));
}
export function judgeMazeMove(board: MazeBoard, from: number, to: number | null, checkpointIndex: number): 'move' | 'wall' | 'locked' {
  if (to === null || !board.passages[from].includes(to)) return 'wall';
  if (to === board.exit && checkpointIndex < board.checkpoints.length) return 'locked';
  return 'move';
}
export function scoreMaze(optimal: number, steps: number, collisions: number, scans: number, completed: boolean) {
  const efficiency = steps > 0 ? clamp(optimal / steps, 0, 1) : 0;
  const accuracy = steps + collisions ? Math.round(steps / (steps + collisions) * 100) : 0;
  // Repeated walking can never inflate score. Only a completed route earns points.
  const score = completed ? Math.round(clamp(60 + efficiency * 30 + 10 - collisions * 3 - scans * 5, 0, 100)) : 0;
  return { score, efficiency: Math.round(efficiency * 100), accuracy };
}
